import { classNames } from 'tradera/utils/class-names';
import type { ChangeEvent, ReactNode } from 'react';
import { createContext, useCallback, useContext } from 'react';
import { useControllableState } from 'tradera/components/form-controls/use-controllable-state';
import styles from './toggle-switch.module.scss';

export type CheckboxToggleProps = {
    onChange?: (checked: boolean) => void;
    id?: string;
    name?: string;
    value?: string;
    disabled?: boolean;
    className?: string;
    label?: ReactNode;
    size?: 'xsmall' | 'small' | 'medium';
} & (
    | {
          checked: boolean;
          defaultChecked?: never;
      }
    | {
          defaultChecked?: boolean;
          checked?: never;
      }
);

/**
 * This component can be controlled or uncontrolled.
 * Pass in `checked` to control the component.
 * When uncontrolled, pass in `defaultChecked` to make component selected.
 *
 * ToggleSwitch2 (now ToggleSwitch) was introduced to smoothly transition from
 * ToggleSwitch's Bootstrap styling to this component's CSS module styling.
 *
 * If you want to break this component apart you can use the `<Root>` and
 * `<Indicator>` components separately.
 */
export const ToggleSwitch = ({
    checked,
    defaultChecked,
    onChange,
    id,
    name,
    value,
    disabled = false,
    className = '',
    label,
    size = 'medium',
    ...props
}: CheckboxToggleProps) => {
    return (
        <Root
            className={classNames(className)}
            {...(checked !== undefined ? { checked } : { defaultChecked })}
            disabled={disabled}
            onChange={onChange}
            id={id}
            name={name}
            value={value}>
            {label}
            <Indicator size={size} {...props} />
        </Root>
    );
};

type CheckboxContextType = {
    checked?: boolean;
    name?: string;
    value?: string;
    disabled?: boolean;
    onChangeHandler?: (event: ChangeEvent<HTMLInputElement>) => void;
    id?: string;
};

const CheckboxContext = createContext<CheckboxContextType | null>(null);

/**
 * This component is the container for the toggle switch. Use it together with
 * the `<Indicator />` component to compose a larger component with a toggle switch
 * inside.
 *
 * This component can be controlled or uncontrolled.
 * Pass in `checked` to control the component.
 * When uncontrolled, pass in `defaultChecked` to make component selected.
 *
 * @example
 * import * as ToggleSwitch from '@tradera/components/form-controls/toggle-switch/toggle-switch';
 * <ToggleSwitch.Root className="border p-6 cursor-pointer bg-gray-100 w-100">
 *   <Space direction="vertical">
 *     <h4>Some heading</h4>
 *     <Space align="center" justify="between">
 *       <Text>
 *         Lorem ipsum dolor sit amet consectetur adipisicing elit.
 *         Quisquam, quos.
 *       </Text>
 *       <ToggleSwitch.Indicator />
 *     </Space>
 *   </Space>
 * </ToggleSwitch.Root>
 */
export const Root = ({
    checked: checkedProp,
    defaultChecked,
    onChange,
    id,
    name,
    value,
    disabled = false,
    className,
    children,
}: React.PropsWithChildren<CheckboxToggleProps>) => {
    const [checked, setChecked] = useControllableState({
        prop: checkedProp,
        defaultProp: defaultChecked,
    });
    const onChangeHandler = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            onChange?.(event.target.checked);
            setChecked(event.target.checked);
        },
        [onChange, setChecked],
    );

    return (
        <CheckboxContext.Provider
            value={{ checked, name, value, disabled, id, onChangeHandler }}>
            <label
                htmlFor={id}
                className={classNames(
                    styles.label,
                    disabled && styles.disabled,
                    className,
                )}>
                {children}
            </label>
        </CheckboxContext.Provider>
    );
};

/**
 * This is the visual indicator of the toggle switch. Use it inside the
 * Root component.
 *
 * @example
 * import * as ToggleSwitch from '@tradera/components/form-controls/toggle-switch/toggle-switch';
 * <ToggleSwitch.Root className="border p-6 cursor-pointer bg-gray-100 w-100">
 *   <Space direction="vertical">
 *     <h4>Some heading</h4>
 *     <Space align="center" justify="between">
 *       <Text>
 *         Lorem ipsum dolor sit amet consectetur adipisicing elit.
 *         Quisquam, quos.
 *       </Text>
 *       <ToggleSwitch.Indicator />
 *     </Space>
 *   </Space>
 * </ToggleSwitch.Root>
 */
export const Indicator = ({
    className = '',
    size = 'medium',
}: {
    className?: string;
    size?: 'xsmall' | 'small' | 'medium';
}) => {
    const context = useContext(CheckboxContext);

    if (!context) {
        throw new Error(
            'ToggleSwitch.Indicator must be used within a ToggleSwitch.Root component',
        );
    }

    const {
        id,
        name,
        value,
        disabled,
        onChangeHandler,
        checked = false,
    } = context;

    return (
        <div
            className={classNames(
                styles.wrapper,
                size === 'small' && styles.small,
                size === 'xsmall' && styles.xsmall,
                className,
            )}>
            <input
                type="checkbox"
                className={classNames(styles.input)}
                name={name}
                id={id}
                value={value}
                checked={checked}
                onChange={onChangeHandler}
                disabled={disabled}
            />
            <span className={styles.switch} />
        </div>
    );
};
